/**
 * Client ของงานขาย — ใบสั่งขาย (`/v1/sales-orders`, D-090) ตาม payload contract ที่
 * freeze ไว้ใน `docs/API_CONTRACT.md` หัวข้อ "Sales Order (`SALES`) — Phase 2"
 *
 * **ฝาแฝดของ `lib/procurement/client.ts` ไม่ใช่ผู้ใช้ของมัน** — โครง ชื่อฟังก์ชัน
 * และชุดความล้มเหลวถูกเขียนใหม่ทั้งชุดที่นี่โดยตั้งใจ ไม่ได้ import ข้ามกลุ่ม:
 * ทั้งสองเป็นคนละโมดูลสิทธิ์ (`SALES` กับ `PROCUREMENT`) คนละชุดกฎธุรกิจ และคนละ
 * ฝั่งของบริษัท การผูกให้ใช้ abstraction ร่วมกันแปลว่าวันที่ contract ฝั่งหนึ่ง
 * เปลี่ยน อีกฝั่งจะเปลี่ยนตามโดยไม่มีใครตั้งใจ
 *
 * หลักการเดียวกับ client อื่นทุกตัวในโปรเจกต์นี้:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "สำเร็จ" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` **ไม่ใช่ "ยังไม่มีใบสั่งขาย"** — รายการ
 *   ว่างกับโหลดไม่สำเร็จต้องแยกกันให้ขาด ไม่งั้นผู้ใช้จะอ่านว่าลูกค้ายังไม่ได้สั่ง
 *   อะไรแล้วคีย์ Customer PO ใบเดิมซ้ำเข้าไปอีกใบ
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * contract ของกลุ่มนี้ freeze ไว้ **ห้า** operation และไฟล์นี้ implement ครบทั้งห้า
 * — ต่างจาก `lib/procurement` ที่เว้น `GET /{id}` ไว้ เพราะที่นี่ `GET /{so_id}`
 * เป็นเส้นเดียวที่ **มีบรรทัด**: รายการตัด `lines` ออกตาม contract การดูว่าลูกค้า
 * สั่งอะไรมาบ้างจึงต้องผ่านเส้นนั้น ไม่ใช่ผ่านแถวในรายการ
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreateSalesOrderBody,
  SalesOrder,
  SalesOrderLine,
  SalesOrderListItem,
  SalesOrderListPage,
  SalesOrderSummary,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';
import { downloadFile, uploadFile } from '../http/fileTransfer';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ
 */
export const SALES_ORDERS_PATH = '/v1/sales-orders';

/**
 * เส้นทางของใบเดียว — `so_id` เป็น **UUID surrogate** (D-039) ไม่ใช่ `so_number`
 * ซึ่งเป็น business key ที่คนอ่านออก
 *
 * `encodeURIComponent` ไว้ด้วยแม้ UUID จะไม่มีอักขระที่ต้อง escape: ค่าที่เดินทางมา
 * ถึงที่นี่มาจาก payload ของเซิร์ฟเวอร์ และค่าที่อ่านมาแล้วแปะลง URL ดิบ ๆ คือวิธีที่
 * คำขอจะเปลี่ยน**เส้นทาง**ทั้งเส้นในวันที่ payload ไม่ใช่สิ่งที่คิดไว้ — ซึ่งในกลุ่มนี้
 * แปลว่า `POST` ที่ตั้งใจจะ "ยืนยัน" ไปโผล่ที่เส้น "ยกเลิก"
 */
export function salesOrderPath(soId: string): string {
  return `${SALES_ORDERS_PATH}/${encodeURIComponent(soId)}`;
}

/** สอง transition ของ lifecycle — ชื่อ segment ตรงตามที่ contract เขียนไว้ */
export type SalesOrderTransition = 'confirm' | 'cancel';

export function salesOrderTransitionPath(
  soId: string,
  transition: SalesOrderTransition,
): string {
  return `${salesOrderPath(soId)}/${transition}`;
}

/**
 * พารามิเตอร์ของ `GET /v1/sales-orders` ที่ contract รับ — **สิบตัวตั้งแต่ D-118**
 *
 * **`q` คือการค้นหาฝั่งเซิร์ฟเวอร์** (D-118 ข้อ 2) ก่อนหน้านี้กลุ่มนี้ไม่มี
 * พารามิเตอร์ค้นหาอิสระเลย ช่องค้นหาจึงกรองจากหน้าที่โหลดมาแล้ว — ซึ่งกับ pagination
 * คือการค้นหา **แค่หน้าจอ** ใบที่อยู่หน้าถัดไปจะถูกรายงานว่า "ไม่พบ" ทั้งที่มีอยู่
 * เซิร์ฟเวอร์เทียบ `q` (ไม่สนตัวพิมพ์ ที่ตำแหน่งใดก็ได้) กับ `so_number`, รหัสและชื่อ
 * ลูกค้า และ `customer_po_number`
 *
 * **สองช่วงวันที่เป็นคนละเรื่องกัน** `created_from`/`created_to` คือ **วันที่เราบันทึก
 * ใบ** (อ่านตามปฏิทินไทย D-055) ส่วน `delivery_from`/`delivery_to` เก็บใบที่ **บรรทัด
 * ใดบรรทัดหนึ่ง** ต้องการของในช่วงนั้น เพราะหัวใบสั่งขายไม่มีวันส่งของตัวเอง (D-118
 * ข้อ 3) ปลายข้างเดียวส่งมาลำพังได้ และช่วงกลับหัวตอบ `422`
 *
 * `plant_code` **แคบลงได้เท่านั้น ไม่เคยกว้างขึ้น** — เซิร์ฟเวอร์กรองตาม
 * `plant_scope` ของผู้เรียกก่อนเสมอ และโรงงานนอกขอบเขตตอบ `403` ไม่ใช่หน้าว่าง
 */
export interface SalesOrderListQuery {
  readonly plant_code?: string;
  readonly customer_code?: string;
  readonly status?: string;
  /** คำค้นอิสระที่ **เซิร์ฟเวอร์** เทียบ (D-118 ข้อ 2) ไม่ใช่ตัวกรองฝั่งเบราว์เซอร์ */
  readonly q?: string;
  /** `YYYY-MM-DD` — วันแรกที่ใบอาจถูกบันทึก (รวมวันนั้น) */
  readonly created_from?: string;
  readonly created_to?: string;
  /** `YYYY-MM-DD` — เก็บใบที่มีบรรทัดต้องการของตั้งแต่วันนี้ขึ้นไป (รวมวันนั้น) */
  readonly delivery_from?: string;
  readonly delivery_to?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * ประกอบ query string — คีย์ที่ไม่ได้ระบุ (หรือว่างเปล่า) ไม่เดินทางไปเลย
 *
 * `cursor` ที่เป็นสตริงว่างถูกทิ้ง ไม่ใช่ส่งไปเป็นค่าว่าง: cursor ว่างไม่ใช่ "หน้าแรก"
 * สำหรับเซิร์ฟเวอร์ แต่เป็นค่าที่มันจะปฏิเสธว่าไม่ใช่ cursor ที่ตนออกให้ — และ `q`
 * ที่เป็นสตริงว่างก็เช่นกัน (`min_length=1` ฝั่งเซิร์ฟเวอร์ตอบ `422` ให้คำค้นว่าง
 * ทั้งที่ผู้ใช้แค่ลบคำค้นทิ้ง)
 */
export function buildSalesOrderListQuery(query: SalesOrderListQuery = {}): string {
  const params = new URLSearchParams();
  // ลำดับคีย์ตรงตามลำดับที่ router ประกาศพารามิเตอร์ไว้ (`sales/router.py`) เพื่อให้
  // URL ที่เทสต์อ่านและ URL ที่คนอ่านใน devtools เรียงเหมือนกันทุกครั้ง
  for (const [key, raw] of [
    ['plant_code', query.plant_code],
    ['customer_code', query.customer_code],
    ['status', query.status],
    ['q', query.q],
    ['created_from', query.created_from],
    ['created_to', query.created_to],
    ['delivery_from', query.delivery_from],
    ['delivery_to', query.delivery_to],
  ] as const) {
    const value = raw?.trim() ?? '';
    if (value !== '') {
      params.append(key, value);
    }
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /**
   * `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ ในกลุ่มนี้มันเป็นตัวเดียวที่แยก `SO_NUMBER_TAKEN`
   * ออกจาก `CUSTOMER_PO_ALREADY_ORDERED` — ทั้งคู่มาด้วย `409` เท่ากันแต่ทางแก้
   * คนละเรื่องกันโดยสิ้นเชิง (เปลี่ยนเลขใบของเรา กับ ใบนี้ถูกรับไปแล้วอย่าคีย์ซ้ำ)
   */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * `403` ของกลุ่มนี้มีสองความหมายที่ `details.rule` เป็นตัวแยก: `PLANT_OUTSIDE_SCOPE`
 * คือ **โรงงานที่ขอไม่อยู่ในขอบเขตของคุณ** (D-004) ส่วนไม่มี rule คือ **แผนกของคุณ
 * ไม่ได้ถือโมดูล `SALES` ในระดับนั้น** — คนละเรื่องกันคนละทางแก้ และเพราะ `SALES`
 * เป็นโมดูลธุรกิจปกติ `ADMIN` ก็ถูกปฏิเสธด้วย `403` เช่นกัน (D-060)
 *
 * `conflict` (`409`) ต้องเป็นสถานะของตัวเอง **ไม่ใช่ `server-error`**: มันคือ
 * "เลขใบซ้ำ" "ใบ PO ของลูกค้าถูกรับไปแล้ว" หรือ "มีคนกดไปก่อนแล้ว" ซึ่งผู้ใช้จัดการ
 * ต่อเองได้ ส่วน "เซิร์ฟเวอร์ทำงานไม่สำเร็จ" คือสิ่งที่เขาทำอะไรไม่ได้เลย
 *
 * `rule-violation` (`422`) เป็นที่อยู่ของกฎที่แพงที่สุดในกลุ่มนี้ —
 * `CONFIRMER_MUST_DIFFER_FROM_CREATOR` (D-010) และ `CUSTOMER_IS_BLOCKED` การกลืน
 * ทั้งสองเป็น `forbidden` จะส่งผู้กดไปขอสิทธิ์เพิ่มเพื่อแก้สิ่งที่ไม่ใช่เรื่องสิทธิ์
 */
export type SalesOrderFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type SalesOrderListResult =
  | { readonly status: 'ready'; readonly page: SalesOrderListPage }
  | SalesOrderFailure;

/** คำตอบของเส้นที่คืนใบ **พร้อมบรรทัด** — `GET /{so_id}` และ `POST` */
export type SalesOrderDetailResult =
  | { readonly status: 'ready'; readonly order: SalesOrder }
  | SalesOrderFailure;

/**
 * คำตอบของ transition — หัวใบฉบับล่าสุด **โดยไม่ยืนยันว่ามีบรรทัดมาด้วย**
 *
 * contract เขียนรูปคำตอบของ `confirm`/`cancel` ไว้แค่โดยนัย (สถานะเปลี่ยนไปที่ใด)
 * ไม่ได้ประกาศว่าบรรทัดติดมาด้วยหรือไม่ การบังคับว่าต้องมีจะทำให้ปุ่มที่ทำงาน
 * **สำเร็จแล้ว** รายงานกลับเป็น `invalid-response` ถ้าเซิร์ฟเวอร์ตอบมาแบบหัวใบล้วน
 * — ซึ่งอ่านเหมือนใบไม่ถูกยืนยัน ทั้งที่ถูกยืนยันไปแล้วจริง ๆ
 */
export type SalesOrderWriteResult =
  | { readonly status: 'ready'; readonly order: SalesOrderSummary }
  | SalesOrderFailure;

export interface SalesOrderRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ: คีย์ที่หายไปทั้งคีย์
 * ถือเป็น `null` ส่วนคีย์ที่มาเป็นตัวเลขหรือ object คือ payload ที่หน้าจอไม่ควรแสดง
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * `next_cursor` ของ envelope — `undefined` แปลว่า "ผิดรูป" ไม่ใช่ "ไม่มีหน้าถัดไป"
 *
 * cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่างจะวน
 * ขอหน้าเดิมซ้ำไปเรื่อย ๆ
 */
function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

/**
 * หนึ่งบรรทัด — **บังคับครบทุกฟิลด์ที่ contract เอ่ยชื่อไว้**
 *
 * ทั้งห้าฟิลด์ที่บังคับคือฟิลด์ที่ `POST` body ของ contract เขียนไว้ตรง ๆ ส่วน
 * `product_id` เป็น UUID คู่ของรหัสซึ่ง contract ไม่ได้ประกาศรูป response ไว้ จึง
 * ปล่อยให้หายไปทั้งคีย์ได้
 *
 * `quantity` ถูกอ่านเป็นสตริงตามที่ส่งมา **ไม่แปลงเป็น number แม้แต่ชั่วคราว** —
 * `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double เก็บได้ครบ
 */
export function parseSalesOrderLine(raw: unknown): SalesOrderLine | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const customerPoLineNo = requiredString(record.customer_po_line_no);
  const productCode = requiredString(record.product_code);
  const quantity = requiredString(record.quantity);
  const uomCode = requiredString(record.uom_code);
  const requestedDeliveryDate = requiredString(record.requested_delivery_date);

  if (
    customerPoLineNo === null ||
    productCode === null ||
    quantity === null ||
    uomCode === null ||
    requestedDeliveryDate === null
  ) {
    return null;
  }

  const productId = nullableString(record.product_id);
  const ref1 = nullableString(record.ref1);
  const ref2 = nullableString(record.ref2);
  const ref3 = nullableString(record.ref3);
  const ref4 = nullableString(record.ref4);
  const ref5 = nullableString(record.ref5);
  const ref6 = nullableString(record.ref6);
  if (
    productId === undefined ||
    ref1 === undefined ||
    ref2 === undefined ||
    ref3 === undefined ||
    ref4 === undefined ||
    ref5 === undefined ||
    ref6 === undefined
  ) {
    return null;
  }

  return {
    customer_po_line_no: customerPoLineNo,
    product_id: productId,
    product_code: productCode,
    quantity,
    uom_code: uomCode,
    requested_delivery_date: requestedDeliveryDate,
    ref1,
    ref2,
    ref3,
    ref4,
    ref5,
    ref6,
  };
}

/**
 * หัวใบหนึ่งใบ **โดยไม่แตะคีย์ `lines` เลย** — ใช้กับทั้งแถวในรายการและคำตอบของ
 * transition
 *
 * **หกฟิลด์ที่บังคับคือหกฟิลด์ที่ contract เอ่ยชื่อไว้ตรง ๆ** (`so_id` จากเส้นทาง
 * `/{so_id}`, `so_number`, `plant_code`, `customer_code`, `customer_po_number` จาก
 * `POST` body และ `status` จาก lifecycle) ส่วนที่เหลือเป็น nullable ที่หายไปทั้งคีย์
 * ได้ — และนั่นเป็นการตัดสินใจ ไม่ใช่ความหละหลวม:
 *
 * contract ของกลุ่มนี้ **ไม่มีตัวอย่าง response ทีละฟิลด์** (ต่างจากกลุ่มอื่นที่มี)
 * การบังคับชื่อฟิลด์ประกอบที่เดาเอาเอง เช่น `confirmed_by_employee_id` หรือ
 * `plant_id` แล้วเซิร์ฟเวอร์ตั้งชื่อไว้ต่างออกไป จะทำให้ **ทั้งรายการ** กลายเป็น
 * `invalid-response` ทั้งที่ทุกอย่างทำงานถูกต้อง ผู้ใช้จะเห็นหน้าจอที่บอกว่าข้อมูล
 * ผิดรูปแทนที่จะเห็นใบสั่งขายของตัวเอง ส่วนความเสี่ยงฝั่งตรงข้าม (ฟิลด์ประกอบหาย
 * ไปจริง) มีผลแค่ค่าที่หน้าจอไม่ได้แสดงอยู่แล้ว
 *
 * `status` ถูกอ่านเป็นสตริงอะไรก็ได้ที่ไม่ว่าง ไม่ใช่ union ของหกค่า: CHECK
 * constraint ฝั่งเซิร์ฟเวอร์เป็นผู้ถือชุดที่รับได้ และการกรองค่าที่เจ็ดทิ้งตรงนี้จะ
 * ทำให้ **ใบที่มีอยู่จริงหายไปจากรายการ** ในวันที่ชุดนั้นถูกขยาย ซึ่งแย่กว่าการแสดง
 * รหัสที่หน้าจอยังไม่มีคำอธิบายให้ (ดู `salesOrderStatusLabel`)
 */
export function parseSalesOrderSummary(raw: unknown): SalesOrderSummary | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const soId = requiredString(record.so_id);
  const soNumber = requiredString(record.so_number);
  const plantCode = requiredString(record.plant_code);
  const customerCode = requiredString(record.customer_code);
  const customerPoNumber = requiredString(record.customer_po_number);
  const status = requiredString(record.status);

  if (
    soId === null ||
    soNumber === null ||
    plantCode === null ||
    customerCode === null ||
    customerPoNumber === null ||
    status === null
  ) {
    return null;
  }

  const plantId = nullableString(record.plant_id);
  const customerId = nullableString(record.customer_id);
  const createdBy = nullableString(record.created_by_employee_id);
  const confirmedBy = nullableString(record.confirmed_by_employee_id);
  const confirmedAt = nullableString(record.confirmed_at);
  const cancelledAt = nullableString(record.cancelled_at);
  const note = nullableString(record.note);
  const createdAt = nullableString(record.created_at);
  const updatedAt = nullableString(record.updated_at);

  if (
    plantId === undefined ||
    customerId === undefined ||
    createdBy === undefined ||
    confirmedBy === undefined ||
    confirmedAt === undefined ||
    cancelledAt === undefined ||
    note === undefined ||
    createdAt === undefined ||
    updatedAt === undefined
  ) {
    return null;
  }

  return {
    so_id: soId,
    so_number: soNumber,
    plant_id: plantId,
    plant_code: plantCode,
    customer_id: customerId,
    customer_code: customerCode,
    customer_po_number: customerPoNumber,
    status,
    created_by_employee_id: createdBy,
    confirmed_by_employee_id: confirmedBy,
    confirmed_at: confirmedAt,
    cancelled_at: cancelledAt,
    note,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * ใบเต็มใบ **พร้อมบรรทัด** — `GET /{so_id}` และ `201` ของ `POST`
 *
 * **บรรทัดต้องอ่านออกทุกบรรทัด ไม่งั้นทั้งใบผิดรูป** บรรทัดที่หายไปเงียบ ๆ หนึ่ง
 * บรรทัดคือคำสั่งซื้อของลูกค้าที่หน้าจอบอกว่าสั่งน้อยกว่าที่เขาสั่งจริง แล้วของที่
 * ขาดไปจะไม่มีใครผลิตและไม่มีใครส่ง
 */
export function parseSalesOrder(raw: unknown): SalesOrder | null {
  const summary = parseSalesOrderSummary(raw);
  if (summary === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.lines)) {
    return null;
  }

  const lines: SalesOrderLine[] = [];
  for (const entry of record.lines) {
    const line = parseSalesOrderLine(entry);
    if (line === null) {
      return null;
    }
    lines.push(line);
  }

  return { ...summary, lines };
}

/**
 * หนึ่งแถวของรายการ — หัวใบ **บวกสี่ค่าที่ D-118 ข้อ 4 เพิ่มเข้ามา**
 *
 * สามในสี่ถูก **บังคับ** (`created_date`, `line_count` และการที่สองฟิลด์ช่วงวันส่งต้อง
 * เป็นสตริงหรือ `null`) ต่างจากฟิลด์ประกอบของหัวใบที่ปล่อยหายไปทั้งคีย์ได้ — เพราะ
 * `SalesOrderListItemResponse` **เอ่ยชื่อทั้งสี่ไว้ตรง ๆ และไม่มี default** (ดู
 * `sales/schemas.py`) และเพราะทั้งสามเป็นค่าที่ **ตัวกรองสองชุดใหม่กรองด้วย**: คอลัมน์
 * ที่หายไปเงียบ ๆ จะทำให้ผู้ใช้อ่านช่องว่างแล้วสรุปว่าตัวกรองช่วงวันที่ไม่ทำงาน แทนที่
 * จะเห็นว่า payload ผิดรูป
 *
 * ช่วงวันส่งเป็น `null` ได้ทั้งคู่ (ใบที่ไม่มีบรรทัด ซึ่งไม่มี route ใดสร้างได้) แต่
 * ชนิดอื่นที่ไม่ใช่สตริงหรือ `null` คือผิดรูป — `nullableString` เป็นผู้แยกสองอย่างนั้น
 */
export function parseSalesOrderListItem(raw: unknown): SalesOrderListItem | null {
  const summary = parseSalesOrderSummary(raw);
  if (summary === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const createdDate = requiredString(record.created_date);
  const lineCount = record.line_count;
  const deliveryFrom = nullableString(record.delivery_date_from);
  const deliveryTo = nullableString(record.delivery_date_to);

  if (
    createdDate === null ||
    typeof lineCount !== 'number' ||
    deliveryFrom === undefined ||
    deliveryTo === undefined
  ) {
    return null;
  }

  return {
    ...summary,
    created_date: createdDate,
    delivery_date_from: deliveryFrom,
    delivery_date_to: deliveryTo,
    line_count: lineCount,
  };
}

/**
 * อ่านรายการจาก envelope `{ items, next_cursor, total }` — **และจากที่นั่นเท่านั้น**
 *
 * contract freeze รูปนี้ไว้ตรง ๆ จึงไม่รับ array เปล่าเป็นทางถอย
 *
 * แต่ละ item ถูกอ่านด้วย `parseSalesOrderListItem()` ซึ่ง **ไม่แตะคีย์ `lines` เลย**
 * ต่อให้เซิร์ฟเวอร์ส่งมาด้วยก็ตาม — contract ตัดบรรทัดออกจากรายการ และหน้าจอที่
 * แอบใช้บรรทัดจากรายการได้ในวันหนึ่ง จะพังเงียบ ๆ ในวันที่เซิร์ฟเวอร์เลิกส่งมา
 *
 * **`total` ถูกบังคับให้เป็นตัวเลข ไม่มีทางถอยเป็น `0`** — ค่าเริ่มต้นที่เดาให้จะทำให้
 * หน้าที่มีแถวอยู่เต็มแสดงว่า "แถว 1–25 จาก 0" แทนที่จะรายงานว่า payload ผิดรูป
 *
 * แถวเดียวที่อ่านไม่ออกทำให้ **ทั้งหน้าผิดรูป** ไม่ใช่ถูกตัดทิ้งเงียบ ๆ: รายการที่
 * ขาดไปหนึ่งแถวคือหน้าจอที่บอกว่ายังไม่ได้รับ Customer PO ใบนั้น แล้วผู้ใช้จะคีย์ซ้ำ
 */
export function parseSalesOrderListPage(raw: unknown): SalesOrderListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items) || typeof record.total !== 'number') {
    return null;
  }

  const items: SalesOrderListItem[] = [];
  for (const entry of record.items) {
    const parsed = parseSalesOrderListItem(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined
    ? null
    : { items, next_cursor: cursor, total: record.total };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ---------------------------------------------------------- serializing -- */

/**
 * แปลง body ของ `POST /v1/sales-orders` ให้เหลือเฉพาะคีย์ที่ contract รับ
 *
 * เขียนคีย์ที่ออกไปทีละตัวแทนการ spread state ของฟอร์ม: `extra = "forbid"` ฝั่ง
 * เซิร์ฟเวอร์ทำให้คีย์แปลกปลอมที่ติดมา (เช่นแถวของใบที่ถูก spread ทั้งก้อนพร้อม
 * `status` หรือ `so_id`) กลายเป็น `400` ทั้งที่ผู้ใช้กรอกถูกทุกช่อง — และเป็นด่านที่
 * ทำให้ฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของไปถึงเซิร์ฟเวอร์ **ไม่ได้เลย** ต่อให้ผู้เรียก
 * ถือ object ที่มีคีย์นั้นอยู่ในมือ
 *
 * ที่แพงที่สุดคือสองคีย์นี้: `status` ที่หลุดไปแปลว่ามีคนพยายามข้าม workflow (ใบที่
 * เกิดมาเป็น `CONFIRMED` เลยคือใบที่ไม่มีใครยืนยัน) และ `created_by_employee_id`
 * ที่หลุดไปแปลว่า D-010 ถูกเทียบกับค่าที่ client แต่งขึ้น
 *
 * **ไม่มี `so_number`** (D-113, D-116 ข้อ 6) — `CreateSalesOrderBody` ไม่มีคีย์นี้ให้
 * ใส่ตั้งแต่ชนิดข้อมูลแล้ว ทั้งสองเส้น (`/v1/sales-orders` และ
 * `/v1/sales-orders/import`) ตอบ `422 SO_NUMBER_IS_SERVER_ASSIGNED` ถ้าคีย์นี้ยัง
 * ติดมา และเซิร์ฟเวอร์เป็นผู้ออกเลขให้เองเสมอ
 *
 * **บรรทัดก็ถูกประกอบใหม่ทีละคีย์เช่นกัน** ไม่ใช่ส่งต่อทั้ง object ที่ผู้เรียกถือ
 * — `ref1`..`ref6` ถูกส่งเป็น `null` ตรง ๆ เมื่อไม่มีค่า (D-107 ข้อ 2) **ไม่ใช่ `""`**
 * เพราะ backend ต้องแยก "ไม่ได้บันทึก" ออกจาก "บันทึกเป็นค่าว่าง" ให้ขาดจากกัน
 *
 * `note` หายไปทั้งคีย์เมื่อไม่ได้ระบุ ไม่ใช่ส่ง `null` หรือสตริงว่าง
 */
export function serializeCreateSalesOrderBody(
  body: CreateSalesOrderBody,
): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    customer_code: body.customer_code,
    customer_po_number: body.customer_po_number,
    ...(body.note === undefined ? {} : { note: body.note }),
    lines: body.lines.map((line) => ({
      customer_po_line_no: line.customer_po_line_no,
      product_code: line.product_code,
      quantity: line.quantity,
      uom_code: line.uom_code,
      requested_delivery_date: line.requested_delivery_date,
      ref1: line.ref1,
      ref2: line.ref2,
      ref3: line.ref3,
      ref4: line.ref4,
      ref5: line.ref5,
      ref6: line.ref6,
    })),
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | SalesOrderFailure;

/**
 * แปล `Response` เป็นความล้มเหลวของกลุ่มนี้ตามสถานะ HTTP — คืน `null` เมื่อสำเร็จ
 *
 * แยกออกมาจาก `requestPayload()` เพื่อให้เส้นทางอัปโหลด (`importSalesOrderFromSpreadsheet`)
 * ใช้ชุดกฎเดียวกันได้ ทั้งที่คำขอของมันเดินผ่าน `lib/http/fileTransfer.ts` ไม่ใช่ผ่าน
 * ฟังก์ชันนี้ — สองเส้นทางต้องแปลรหัสสถานะเดียวกันเป็นความล้มเหลวเดียวกันเสมอ
 */
async function failureFromResponse(response: Response): Promise<SalesOrderFailure | null> {
  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }
  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }
  return null;
}

async function requestPayload(
  path: string,
  init: RequestInit,
  options: SalesOrderRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  const failure = await failureFromResponse(response);
  if (failure !== null) {
    return failure;
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** คำตอบของเส้นที่ contract สัญญาว่ามีบรรทัดครบ */
function toDetailResult(result: PayloadResult): SalesOrderDetailResult {
  if (result.status !== 'payload') {
    return result;
  }
  const order = parseSalesOrder(result.payload);
  return order === null ? { status: 'invalid-response' } : { status: 'ready', order };
}

/** คำตอบของ transition — หัวใบฉบับล่าสุด บรรทัด (ถ้ามี) ไม่ถูกอ่าน */
function toWriteResult(result: PayloadResult): SalesOrderWriteResult {
  if (result.status !== 'payload') {
    return result;
  }
  const order = parseSalesOrderSummary(result.payload);
  return order === null ? { status: 'invalid-response' } : { status: 'ready', order };
}

/**
 * `GET /v1/sales-orders` — **กรองตามขอบเขตโรงงานของผู้เรียกเสมอ** และ **ไม่มีบรรทัด**
 *
 * **ไม่เรียงลำดับซ้ำเอง** การเรียงซ้ำที่ frontend จะกลบกรณีที่เซิร์ฟเวอร์เรียงผิดจน
 * ไม่มีใครเห็น และจะทำให้ cursor ของหน้าถัดไปไม่ต่อกับสิ่งที่ผู้ใช้เห็นอยู่
 */
export async function fetchSalesOrders(
  query: SalesOrderListQuery = {},
  options: SalesOrderRequestOptions = {},
): Promise<SalesOrderListResult> {
  const result = await requestPayload(
    `${SALES_ORDERS_PATH}${buildSalesOrderListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseSalesOrderListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `GET /v1/sales-orders/{so_id}` — ใบเดียว **พร้อมบรรทัดครบ**
 *
 * เส้นเดียวของกลุ่มนี้ที่ตอบคำถาม "ลูกค้าสั่งอะไรมาบ้างในใบนี้" — รายการตัดบรรทัด
 * ออกตาม contract ผู้เรียกจึงต้องมาที่นี่ **ทีละใบที่ผู้ใช้เปิดดูจริง** ไม่ใช่วน
 * เรียกให้ครบทุกแถวของรายการเพื่อประกอบตารางเดียว (N+1 คำขอที่ contract ตั้งใจ
 * หลีกเลี่ยงด้วยการตัดบรรทัดออกตั้งแต่แรก)
 *
 * ใบที่ไม่มีอยู่ หรืออยู่ในโรงงานนอกขอบเขตของผู้เรียก ตอบ `404` เหมือนกันทั้งคู่
 */
export async function fetchSalesOrder(
  soId: string,
  options: SalesOrderRequestOptions = {},
): Promise<SalesOrderDetailResult> {
  return toDetailResult(
    await requestPayload(
      salesOrderPath(soId),
      { method: 'GET', headers: { Accept: 'application/json' } },
      options,
    ),
  );
}

/**
 * `POST /v1/sales-orders` — รับ Customer PO เข้าเป็นใบใหม่ที่ `DRAFT` ตอบ `201`
 *
 * **หัวใบและบรรทัดในคำขอเดียว** และการรับเข้าเป็น all-or-nothing: ถูกปฏิเสธเมื่อไร
 * ก็ไม่มีใบและไม่มีบรรทัดใดถูกเขียนเลย (`CUSTOMER_PO_ALREADY_ORDERED` ระบุตรง ๆ ว่า
 * "ไม่เขียนอะไรเลย")
 *
 * **ไม่มี `Idempotency-Key`**: contract สงวน header นั้นไว้ให้ Scan, Goods Receipt,
 * Initial Import และ Offline Sync ที่กระทบ stock/บัญชี การรับคำสั่งซื้อเข้าระบบยัง
 * ไม่เคลื่อนสต๊อกหรือบัญชี และการคีย์ซ้ำถูกกันด้วย unique constraint สองชั้นอยู่แล้ว
 * (`409 SO_NUMBER_TAKEN` และ `409 CUSTOMER_PO_ALREADY_ORDERED`)
 */
export async function createSalesOrder(
  body: CreateSalesOrderBody,
  options: SalesOrderRequestOptions = {},
): Promise<SalesOrderDetailResult> {
  return toDetailResult(
    await requestPayload(
      SALES_ORDERS_PATH,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeCreateSalesOrderBody(body)),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/sales-orders/import` (D-092) — **เส้นเดียวที่หน้ารับใบสั่งซื้อของลูกค้า
 * ใช้ส่งข้อมูลจริง** ทั้งกรอกมือและหลัง upload (D-116 ข้อ 9)
 *
 * payload เดียวกับ `createSalesOrder()` เป๊ะ แต่คำตอบต่างกัน: เส้นนี้ตัดสินสามทาง
 * จาก `(customer, customer_po_number)` แทนที่จะตอบ `409
 * CUSTOMER_PO_ALREADY_ORDERED` เมื่อเจอ Customer PO ซ้ำ — ผลจริง (`outcome`) มากับ
 * `200`/`201` เสมอ ไม่ใช่ error response จึงถูกอ่านด้วย `parseCustomerPoImportResult`
 * ตัวเดียวกับที่ `importSalesOrderFromSpreadsheet()` ใช้ (คำตอบรูปเดียวกันเป๊ะ)
 *
 * **ห้ามใช้ `createSalesOrder()` แทนเส้นนี้บนหน้ารับใบ** — หน้าที่ทำนายผลด้วย
 * `previewSalesOrderSpreadsheet()` แล้วส่งไปคนละเส้นคือหน้าที่โกหก (ดู
 * `SalesOrderReceivePage.tsx`)
 */
export async function importCustomerPurchaseOrder(
  body: CreateSalesOrderBody,
  options: SalesOrderRequestOptions = {},
): Promise<SalesOrderSpreadsheetImportResult> {
  const result = await requestPayload(
    `${SALES_ORDERS_PATH}/import`,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateSalesOrderBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const parsed = parseCustomerPoImportResult(result.payload);
  return parsed === null ? { status: 'invalid-response' } : { status: 'ready', result: parsed };
}

/**
 * body ของ transition — **ว่างเปล่าเสมอ**
 *
 * ต่างจากใบขอซื้อ/ใบสั่งซื้อที่ส่ง `expected_status` ไปด้วย: contract ของกลุ่มนี้
 * **ไม่ได้ประกาศฟิลด์ใดในคำขอของ `confirm`/`cancel` เลย** และคีย์ที่ contract ไม่ได้
 * ประกาศ คือคีย์ที่ `extra = "forbid"` จะตอบ `400` ให้ — ปุ่มที่ใช้งานไม่ได้เลยแม้แต่
 * ครั้งเดียว แย่กว่าการไม่มีด่าน staleness ฝั่ง client
 *
 * ผลที่ตามมาและต้องยอมรับ: `409` ของ transition แยก "มีคนกดไปก่อนแล้ว" ออกจาก
 * "เส้นทางนี้ไม่มีอยู่จริง" ไม่ได้ — แต่ `details.current_status` ที่ contract สัญญา
 * ว่ามาด้วยทุกครั้ง บอกสิ่งที่ผู้ใช้ต้องรู้จริง ๆ อยู่แล้ว คือใบนี้ **อยู่ที่ไหน**
 */
function transitionBody(): string {
  return JSON.stringify({});
}

async function requestTransition(
  soId: string,
  transition: SalesOrderTransition,
  options: SalesOrderRequestOptions,
): Promise<SalesOrderWriteResult> {
  return toWriteResult(
    await requestPayload(
      salesOrderTransitionPath(soId, transition),
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: transitionBody(),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/sales-orders/{so_id}/confirm` — `DRAFT → CONFIRMED` (EDIT)
 *
 * **ผู้ยืนยันต้องไม่ใช่ผู้เปิดใบ** (D-010) ผู้ที่ถือสิทธิ์ครบแต่เป็นผู้เปิดใบเองจะได้
 * `422 CONFIRMER_MUST_DIFFER_FROM_CREATOR` และ **ไม่มีอะไรถูกเขียนเลย** — คำตอบนั้น
 * เป็น `rule-violation` ไม่ใช่ `forbidden` ตรงตามที่เซิร์ฟเวอร์ตั้งใจแยกไว้
 *
 * สถานะอื่นนอกจาก `DRAFT` ตอบ `409` พร้อม `details.current_status`
 */
export async function confirmSalesOrder(
  soId: string,
  options: SalesOrderRequestOptions = {},
): Promise<SalesOrderWriteResult> {
  return requestTransition(soId, 'confirm', options);
}

/**
 * `POST /v1/sales-orders/{so_id}/cancel` — `DRAFT|CONFIRMED → CANCELLED` (EDIT)
 *
 * ใบที่ยกเลิกไปแล้ว หรือที่ส่งของไปแล้ว **ยกเลิกไม่ได้** (`409` พร้อม
 * `details.current_status`) — ของออกจากโรงงานไปแล้วจริง ๆ การถอนต้องเป็นเอกสารคืน
 * ของ ไม่ใช่การลบคำสั่งซื้อทิ้ง
 */
export async function cancelSalesOrder(
  soId: string,
  options: SalesOrderRequestOptions = {},
): Promise<SalesOrderWriteResult> {
  return requestTransition(soId, 'cancel', options);
}

/**
 * อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี
 *
 * ไม่เดาค่าเมื่ออ่านไม่ได้ เพราะกฎที่เดาผิดจะส่งผู้ใช้ไปแก้เรื่องที่ไม่ได้เสีย
 */
export function ruleFromFailure(failure: SalesOrderFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/**
 * อ่าน `details.current_status` จากความล้มเหลว — สถานะที่ใบนั้น **ถืออยู่จริง**
 *
 * contract สัญญาว่า `409` ของ transition พามันมาด้วยเสมอ และในกลุ่มนี้มันสำคัญกว่า
 * ที่อื่น เพราะ **ไม่มีรหัสกฎประจำการปฏิเสธ transition** ให้อ่าน — `current_status`
 * จึงเป็นสิ่งเดียวที่ทำให้หน้าจอบอกผู้ใช้ได้ว่าปุ่มที่เขากดไม่ทำงานเพราะอะไร แทนที่
 * จะบอกแค่ว่าไม่สำเร็จ
 */
export function currentStatusFromFailure(failure: SalesOrderFailure): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/* ------------------------------------------- D-107 ข้อ 4 — Excel spreadsheet -- */

/**
 * เส้นทางของทั้งสอง operation ของ D-107 ข้อ 4 — ต่อจาก `/import` ที่มีอยู่แล้ว
 * (D-092) ไม่ใช่กลุ่มที่ห้า: การอัปโหลดแปลงไฟล์เป็น payload เดียวกับ `/import` แล้ว
 * เรียก service เดียวกัน (`apps/api/.../sales/router.py`)
 */
export const SALES_ORDER_IMPORT_SPREADSHEET_PATH = `${SALES_ORDERS_PATH}/import-spreadsheet`;
export const SALES_ORDER_IMPORT_TEMPLATE_PATH = `${SALES_ORDER_IMPORT_SPREADSHEET_PATH}/template`;

/**
 * media type ของ `.xlsx` ทั้งสองทาง — ค่าเดียวกับ `spreadsheet.XLSX_MEDIA_TYPE`
 * ฝั่งเซิร์ฟเวอร์ (`apps/api/.../sales/spreadsheet.py`)
 */
export const SALES_ORDER_SPREADSHEET_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** ชื่อไฟล์ที่ผู้ใช้เห็นตอนเซฟ — ค่าเดียวกับ `spreadsheet.TEMPLATE_FILENAME` */
export const SALES_ORDER_IMPORT_TEMPLATE_FILENAME = 'sales-order-import-template.xlsx';

/** สามคำตอบที่ D-092 ตัดสิน — สตริงดิบจากเซิร์ฟเวอร์ ไม่ใช่ค่าที่หน้าจอเดาขึ้นเอง */
export type CustomerPoImportOutcome = 'CREATED' | 'DUPLICATE_IGNORED' | 'REVISION_RAISED';

/**
 * คำตอบของ `import-spreadsheet` — ผลลัพธ์ **เท่าที่หน้าจอต้องใช้แสดง** ไม่ใช่ทุกฟิลด์
 * ของ `CustomerPoImportResponse` ฝั่งเซิร์ฟเวอร์ (เช่น `import_id`, `plant_id`,
 * `imported_by_employee_id` ไม่มีที่ใช้บนหน้าจอนี้จึงไม่ถูกบังคับให้อ่านออก)
 */
export interface CustomerPoImportResult {
  readonly outcome: CustomerPoImportOutcome;
  readonly payload_hash: string;
  readonly line_count: number;
  readonly customer_po_number: string;
  /** ใบที่การนำเข้ารอบนี้จบลงที่ — เขียนใหม่บน `CREATED` และคงเดิมบนอีกสองทาง */
  readonly sales_order: SalesOrder;
}

export type SalesOrderSpreadsheetImportResult =
  | { readonly status: 'ready'; readonly result: CustomerPoImportResult }
  | SalesOrderFailure;

/**
 * สามค่าที่ D-092 ตัดสินไว้ — ไม่มีค่าที่สี่ และค่านอกชุดนี้ทำให้ทั้งคำตอบผิดรูป
 *
 * ไม่รับสตริงอะไรก็ได้เหมือน `status` ของหัวใบ (ดู `parseSalesOrderSummary`) เพราะ
 * `outcome` **ไม่มี CHECK constraint หนุนอยู่แบบเดียวกับ status** — มันเป็นค่าที่
 * โค้ดฝั่งเซิร์ฟเวอร์เขียนตรง ๆ จาก enum สามค่า (`CustomerPoImportOutcome` ใน
 * `sales/models.py`) ไม่ใช่ค่าที่ฐานข้อมูลจะขยายได้โดยไม่มีใครเห็น
 */
function isCustomerPoImportOutcome(value: string): value is CustomerPoImportOutcome {
  return value === 'CREATED' || value === 'DUPLICATE_IGNORED' || value === 'REVISION_RAISED';
}

function parseCustomerPoImportResult(raw: unknown): CustomerPoImportResult | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const outcome = requiredString(record.outcome);
  const payloadHash = requiredString(record.payload_hash);
  const customerPoNumber = requiredString(record.customer_po_number);
  const lineCount = record.line_count;

  if (
    outcome === null ||
    !isCustomerPoImportOutcome(outcome) ||
    payloadHash === null ||
    customerPoNumber === null ||
    typeof lineCount !== 'number'
  ) {
    return null;
  }

  const salesOrder = parseSalesOrder(record.sales_order);
  if (salesOrder === null) {
    return null;
  }

  return {
    outcome,
    payload_hash: payloadHash,
    line_count: lineCount,
    customer_po_number: customerPoNumber,
    sales_order: salesOrder,
  };
}

/**
 * `POST /v1/sales-orders/import-spreadsheet` — อัปโหลด `.xlsx` เป็น Customer PO
 *
 * **ส่งไบต์ของไฟล์เป็น body ตรง ๆ ผ่าน `uploadFile()`** ไม่ใช่ `FormData` — เส้นทาง
 * นี้อ่าน body เป็น stream ดิบ (`spreadsheet.read_within_limit`) ไม่ใช่ multipart form
 * การห่อด้วย `FormData` จะได้ `422 SPREADSHEET_IS_NOT_A_WORKBOOK` เสมอไม่ว่าไฟล์จะถูก
 * แค่ไหน (ดู `lib/http/fileTransfer.ts`)
 *
 * ผลลัพธ์แปลด้วยกฎเดียวกับ `requestPayload()` (`failureFromResponse`) แม้คำขอจะไม่ได้
 * เดินผ่านฟังก์ชันนั้นเลยก็ตาม เพราะรหัสสถานะเดียวกันต้องแปลเป็นความล้มเหลวเดียวกัน
 */
export async function importSalesOrderFromSpreadsheet(
  file: Blob,
  options: SalesOrderRequestOptions = {},
): Promise<SalesOrderSpreadsheetImportResult> {
  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, SALES_ORDER_IMPORT_SPREADSHEET_PATH);

  const uploaded = await uploadFile(url, file, SALES_ORDER_SPREADSHEET_MEDIA_TYPE, {
    fetchImpl: options.fetchImpl,
    signal: options.signal,
  });
  if (uploaded.status !== 'ready') {
    return uploaded;
  }

  const failure = await failureFromResponse(uploaded.response);
  if (failure !== null) {
    return failure;
  }

  let payload: unknown;
  try {
    payload = await uploaded.response.json();
  } catch {
    return { status: 'invalid-response' };
  }

  const result = parseCustomerPoImportResult(payload);
  return result === null ? { status: 'invalid-response' } : { status: 'ready', result };
}

/* --------------------------------- D-116 ข้อ 5 — พรีวิวก่อนส่ง ไม่เขียนอะไรเลย -- */

export const SALES_ORDER_IMPORT_SPREADSHEET_PREVIEW_PATH = `${SALES_ORDER_IMPORT_SPREADSHEET_PATH}/preview`;

/**
 * หนึ่งบรรทัดที่พรีวิวอ่านออกมา — เหมือน `SalesOrderLine` แต่มี `row` (เลขแถวใน
 * ไฟล์ที่คนหาเจอ) และไม่มี `product_id` (ยังไม่มีอะไรถูกเขียน จึงไม่มี surrogate
 * key ให้อ้าง — ดู `SalesOrderSpreadsheetPreviewLineResponse` ฝั่งเซิร์ฟเวอร์)
 */
export interface SalesOrderSpreadsheetPreviewLine {
  readonly row: number;
  readonly line_no: number;
  readonly customer_po_line_no: string;
  readonly product_code: string;
  /**
   * `product_general.name` ที่เซิร์ฟเวอร์ resolve ไว้แล้วตอนตรวจไฟล์ (D-117 ข้อ 4,
   * `WO-P2-M4-011`) — **ไม่บังคับให้ client ยิงค้นหาเองทีละรหัส** ไฟล์ 200 บรรทัด
   * จึงไม่กลายเป็น 200 คำขอ และคนที่ upload ได้เห็นชื่อสินค้าแม้ไม่มีสิทธิ์
   * `PRODUCT_MASTER` เลยก็ตาม
   */
  readonly product_name: string;
  /** `product_general.model` — nullable เหมือนที่ Product Master เก็บไว้ (D-107 ข้อ 1) */
  readonly model: string | null;
  readonly quantity: string;
  readonly uom_code: string;
  readonly requested_delivery_date: string;
  readonly ref1: string | null;
  readonly ref2: string | null;
  readonly ref3: string | null;
  readonly ref4: string | null;
  readonly ref5: string | null;
  readonly ref6: string | null;
}

/**
 * คำตอบของ `POST /v1/sales-orders/import-spreadsheet/preview` — **ไม่มีอะไรถูกเขียน
 * ลงจากเส้นนี้เลย** (D-116 ข้อ 5) `would_be` คือผลที่ `POST
 * .../import-spreadsheet` (หรือ `/import` ด้วย payload เดียวกัน) จะตอบ ถ้ากดส่งไฟล์
 * นี้จริง — คำนวณด้วยตัวตัดสินสามทางเดียวกับ D-092 ไม่ใช่การอ่านซ้ำรอบสอง
 *
 * `so_id`/`so_number` เป็น `null` เมื่อ `would_be === 'CREATED'` เท่านั้น — ยังไม่มี
 * ใบและยังไม่มีเลข เพราะเลขถูกออกตอนเขียนแถวจริง (D-113) ไม่ใช่ตอนพรีวิว
 */
export interface SalesOrderSpreadsheetPreview {
  readonly would_be: CustomerPoImportOutcome;
  readonly plant_code: string;
  readonly customer_code: string;
  readonly customer_po_number: string;
  readonly note: string | null;
  readonly so_id: string | null;
  readonly so_number: string | null;
  readonly line_count: number;
  readonly lines: readonly SalesOrderSpreadsheetPreviewLine[];
}

export type SalesOrderSpreadsheetPreviewResult =
  | { readonly status: 'ready'; readonly preview: SalesOrderSpreadsheetPreview }
  | SalesOrderFailure;

function parseSalesOrderSpreadsheetPreviewLine(
  raw: unknown,
): SalesOrderSpreadsheetPreviewLine | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const row = record.row;
  const lineNo = record.line_no;
  const customerPoLineNo = requiredString(record.customer_po_line_no);
  const productCode = requiredString(record.product_code);
  // `product_name` ไม่ null ได้เลย (D-117 ข้อ 4) — บรรทัดที่ถึงคำตอบนี้แปลว่ารหัส
  // สินค้าถูก resolve แล้ว ชื่อที่หายไปคือ payload ผิดรูป ไม่ใช่สินค้าที่ไม่มีชื่อ
  const productName = requiredString(record.product_name);
  const quantity = requiredString(record.quantity);
  const uomCode = requiredString(record.uom_code);
  const requestedDeliveryDate = requiredString(record.requested_delivery_date);

  if (
    typeof row !== 'number' ||
    typeof lineNo !== 'number' ||
    customerPoLineNo === null ||
    productCode === null ||
    productName === null ||
    quantity === null ||
    uomCode === null ||
    requestedDeliveryDate === null
  ) {
    return null;
  }

  const model = nullableString(record.model);
  if (model === undefined) {
    return null;
  }

  const ref1 = nullableString(record.ref1);
  const ref2 = nullableString(record.ref2);
  const ref3 = nullableString(record.ref3);
  const ref4 = nullableString(record.ref4);
  const ref5 = nullableString(record.ref5);
  const ref6 = nullableString(record.ref6);
  if (
    ref1 === undefined ||
    ref2 === undefined ||
    ref3 === undefined ||
    ref4 === undefined ||
    ref5 === undefined ||
    ref6 === undefined
  ) {
    return null;
  }

  return {
    row,
    line_no: lineNo,
    customer_po_line_no: customerPoLineNo,
    product_code: productCode,
    product_name: productName,
    model,
    quantity,
    uom_code: uomCode,
    requested_delivery_date: requestedDeliveryDate,
    ref1,
    ref2,
    ref3,
    ref4,
    ref5,
    ref6,
  };
}

function parseSalesOrderSpreadsheetPreview(raw: unknown): SalesOrderSpreadsheetPreview | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const wouldBe = requiredString(record.would_be);
  const plantCode = requiredString(record.plant_code);
  const customerCode = requiredString(record.customer_code);
  const customerPoNumber = requiredString(record.customer_po_number);
  const lineCount = record.line_count;

  if (
    wouldBe === null ||
    !isCustomerPoImportOutcome(wouldBe) ||
    plantCode === null ||
    customerCode === null ||
    customerPoNumber === null ||
    typeof lineCount !== 'number' ||
    !Array.isArray(record.lines)
  ) {
    return null;
  }

  const note = nullableString(record.note);
  const soId = nullableString(record.so_id);
  const soNumber = nullableString(record.so_number);
  if (note === undefined || soId === undefined || soNumber === undefined) {
    return null;
  }

  const lines: SalesOrderSpreadsheetPreviewLine[] = [];
  for (const entry of record.lines) {
    const line = parseSalesOrderSpreadsheetPreviewLine(entry);
    if (line === null) {
      return null;
    }
    lines.push(line);
  }

  return {
    would_be: wouldBe,
    plant_code: plantCode,
    customer_code: customerCode,
    customer_po_number: customerPoNumber,
    note,
    so_id: soId,
    so_number: soNumber,
    line_count: lineCount,
    lines,
  };
}

/**
 * `POST /v1/sales-orders/import-spreadsheet/preview` — อ่าน `.xlsx` กลับมาบอกว่ากด
 * ส่งแล้วจะเกิดอะไร **โดยไม่เขียนอะไรเลย** (D-116 ข้อ 5)
 *
 * ส่งไบต์ของไฟล์ตรง ๆ ผ่าน `uploadFile()` เหมือน `importSalesOrderFromSpreadsheet()`
 * ทุกประการ (ดูเหตุผลที่ฟังก์ชันนั้น) — เส้นนี้เป็นแค่ read-only ของ parser เดียวกัน
 * และการปฏิเสธไฟล์ (422 พร้อม `details.row`/`details.column`) ก็เป็นชุดเดียวกัน
 */
export async function previewSalesOrderSpreadsheet(
  file: Blob,
  options: SalesOrderRequestOptions = {},
): Promise<SalesOrderSpreadsheetPreviewResult> {
  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, SALES_ORDER_IMPORT_SPREADSHEET_PREVIEW_PATH);

  const uploaded = await uploadFile(url, file, SALES_ORDER_SPREADSHEET_MEDIA_TYPE, {
    fetchImpl: options.fetchImpl,
    signal: options.signal,
  });
  if (uploaded.status !== 'ready') {
    return uploaded;
  }

  const failure = await failureFromResponse(uploaded.response);
  if (failure !== null) {
    return failure;
  }

  let payload: unknown;
  try {
    payload = await uploaded.response.json();
  } catch {
    return { status: 'invalid-response' };
  }

  const preview = parseSalesOrderSpreadsheetPreview(payload);
  return preview === null ? { status: 'invalid-response' } : { status: 'ready', preview };
}

export type DownloadSalesOrderImportTemplateResult =
  | { readonly status: 'ready' }
  | SalesOrderFailure;

/**
 * `GET /v1/sales-orders/import-spreadsheet/template` — ดาวน์โหลดฟอร์มเปล่า
 *
 * **ผ่าน `downloadFile()` ซึ่งใช้ `apiFetch` เป็นค่าเริ่มต้นเมื่อไม่ระบุ `fetchImpl`**
 * เส้นทางนี้ gate ด้วย `SALES` + VIEW (D-107 ข้อ 4) — ปุ่มดาวน์โหลดที่เป็น
 * `<a href="...">` ธรรมดาจะไม่แนบ `Authorization` ไปด้วยเลยและได้ `401` ที่ดูเหมือน
 * ดาวน์โหลดพังโดยไม่มีข้อความ (ดู invariant ของ Work Order นี้)
 */
export async function downloadSalesOrderImportTemplate(
  options: SalesOrderRequestOptions = {},
): Promise<DownloadSalesOrderImportTemplateResult> {
  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, SALES_ORDER_IMPORT_TEMPLATE_PATH);

  const downloaded = await downloadFile(url, SALES_ORDER_IMPORT_TEMPLATE_FILENAME, {
    fetchImpl: options.fetchImpl,
    signal: options.signal,
  });

  switch (downloaded.status) {
    case 'ready':
      return { status: 'ready' };
    case 'cancelled':
      return { status: 'cancelled' };
    case 'network-error':
      return { status: 'network-error', detail: downloaded.detail };
    case 'failed': {
      const failure = await failureFromResponse(downloaded.response);
      return failure ?? { status: 'server-error', httpStatus: downloaded.response.status };
    }
  }
}

/* ------------------------------------ D-118 ข้อ 5-7 — Export เป็น .xlsx ----- */

/**
 * `POST /v1/sales-orders/export` — **`POST` ที่ไม่เขียนอะไรเลย**
 *
 * เส้นเดียวของกลุ่มนี้ที่เป็น `POST` แล้วไม่เขียน และเหตุผลอยู่ที่ body: id ที่ผู้ใช้
 * ติ๊กไว้สองร้อยตัวไม่ลง query string ที่ proxy ทุกตัวระหว่างเบราว์เซอร์กับ service จะ
 * ส่งต่อครบ และ `GET` ที่ความหมายมาจาก request body แย่กว่า `POST` ที่ไม่เขียนอะไร
 */
export const SALES_ORDER_EXPORT_PATH = `${SALES_ORDERS_PATH}/export`;

/** ชื่อไฟล์ที่ผู้ใช้เห็นตอนเซฟ — ค่าเดียวกับ `service.EXPORT_FILENAME` ฝั่งเซิร์ฟเวอร์ */
export const SALES_ORDER_EXPORT_FILENAME = 'sales-orders.xlsx';

/**
 * ครึ่ง "ตัวกรอง" ของ body — **พารามิเตอร์ของรายการเป๊ะตัวต่อตัว ลบ `limit`/`cursor`**
 *
 * สองตัวที่หายไปเป็นเรื่องของ *หน้าหนึ่งหน้า* ไม่ใช่เรื่องของ *สิ่งที่อยู่ในนั้น* —
 * Export เขียนทุกแถวที่ตัวกรองตรง ไม่ใช่แค่หน้าที่เปิดอยู่ (D-118 ข้อ 5)
 */
export type SalesOrderExportFilters = Omit<SalesOrderListQuery, 'limit' | 'cursor'>;

/**
 * body ของ Export — **ทางใดทางหนึ่ง ไม่เคยทั้งคู่** (D-118 ข้อ 6)
 *
 * `so_ids` คือแถวที่ผู้ใช้ติ๊ก ส่วน `filters` คือ "ทุกใบที่ตรงกับสิ่งที่ฉันกำลังดูอยู่"
 * ซึ่งเป็นสิ่งที่หน้าจอส่งเมื่อผู้ใช้กด *เลือกทั้งหมด N* หรือไม่ได้ติ๊กอะไรเลย ชนิดนี้
 * เป็น union จึงไม่มีทางถือทั้งสองคีย์พร้อมกันได้ตั้งแต่ชนิดข้อมูล — ไม่ใช่แค่ "ไม่ส่ง"
 */
export type SalesOrderExportRequest =
  | { readonly so_ids: readonly string[] }
  | { readonly filters: SalesOrderExportFilters };

/**
 * ประกอบ body ทีละคีย์ — เขียนออกไป **คีย์เดียว** เสมอ
 *
 * ตัวกรองที่ว่างถูกตัดทิ้งทั้งคีย์เหมือนตอนประกอบ query string ของรายการ: `filters: {}`
 * คือประโยค "ทุกใบที่ฉันมีสิทธิ์เห็น" ซึ่งเซิร์ฟเวอร์รับ ส่วน `q: ""` คือค่าที่
 * `min_length=1` จะตอบ `422` ให้
 */
export function serializeSalesOrderExportRequest(
  request: SalesOrderExportRequest,
): Record<string, unknown> {
  if ('so_ids' in request) {
    return { so_ids: [...request.so_ids] };
  }
  const filters: Record<string, string> = {};
  for (const [key, raw] of [
    ['plant_code', request.filters.plant_code],
    ['customer_code', request.filters.customer_code],
    ['status', request.filters.status],
    ['q', request.filters.q],
    ['created_from', request.filters.created_from],
    ['created_to', request.filters.created_to],
    ['delivery_from', request.filters.delivery_from],
    ['delivery_to', request.filters.delivery_to],
  ] as const) {
    const value = raw?.trim() ?? '';
    if (value !== '') {
      filters[key] = value;
    }
  }
  return { filters };
}

export type ExportSalesOrdersResult = { readonly status: 'ready' } | SalesOrderFailure;

/**
 * ขอไฟล์ `.xlsx` ของแถวที่ระบุ แล้วให้เบราว์เซอร์เซฟ — **ไม่มีอะไรถูกเขียนที่เซิร์ฟเวอร์**
 *
 * **ยืม `downloadFile()` ทั้งขั้นบันทึกไฟล์ โดยสวม `fetchImpl` ให้เป็น `POST`** —
 * `lib/http/fileTransfer.ts` เป็นที่เดียวในแอปที่แตะ `createObjectURL`/`revokeObjectURL`
 * (ดู docstring ของไฟล์นั้น) และมันยิง `GET` เพราะเส้นดาวน์โหลดเส้นแรกของแอปเป็น `GET`
 * เส้นนี้เป็น `POST` ที่มี body การก็อปขั้น blob → anchor → revoke มาไว้ที่นี่จะได้
 * สำเนาที่สองของกลไกที่ "เรียกแล้วลืม revoke ไม่ได้" — ซึ่งเป็นเหตุผลทั้งหมดที่กลไกนั้น
 * อยู่ที่เดียว `fetchImpl` เป็นรอยต่อที่โมดูลนั้น **เปิดไว้ให้แล้ว** จึงใช้รอยต่อนั้น
 * แทนการเขียนกลไกซ้ำหรือแก้ไฟล์ที่ Agent อื่นเป็นเจ้าของ
 *
 * ความล้มเหลวถูกแปลด้วย `failureFromResponse()` ชุดเดียวกับทุกเส้นของกลุ่มนี้ — `422`
 * `SALES_ORDER_EXPORT_TOO_MANY_ROWS` จึงมาถึงหน้าจอเป็น `rule-violation` พร้อมข้อความ
 * ของเซิร์ฟเวอร์ ไม่ใช่ "ดาวน์โหลดพัง" ที่ไม่บอกอะไร
 */
export async function exportSalesOrders(
  request: SalesOrderExportRequest,
  options: SalesOrderRequestOptions = {},
): Promise<ExportSalesOrdersResult> {
  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, SALES_ORDER_EXPORT_PATH);
  const body = JSON.stringify(serializeSalesOrderExportRequest(request));
  const fetchImpl = options.fetchImpl ?? apiFetch;

  const downloaded = await downloadFile(url, SALES_ORDER_EXPORT_FILENAME, {
    fetchImpl: ((input: RequestInfo | URL, init?: RequestInit) =>
      fetchImpl(input, {
        ...init,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      })) as typeof fetch,
    ...(options.signal ? { signal: options.signal } : {}),
  });

  switch (downloaded.status) {
    case 'ready':
      return { status: 'ready' };
    case 'cancelled':
      return { status: 'cancelled' };
    case 'network-error':
      return { status: 'network-error', detail: downloaded.detail };
    case 'failed': {
      const failure = await failureFromResponse(downloaded.response);
      return failure ?? { status: 'server-error', httpStatus: downloaded.response.status };
    }
  }
}
